<template>
  <input-dropdown
    v-if="availableApplications.length"
    v-model="currentApplicationId"
    :options="availableApplications"
    :offset="[xOffset, 5]"
  >
    <template #trigger="{ showDropdown }">
      <div
        class="flex cursor-pointer items-center space-x-2 text-xl font-semibold text-branding-heading"
        data-cy="selection"
      >
        <span v-if="selectedApplication">{{ selectedApplication.business_legal_name }}</span>
        <icon-arrow :class="showDropdown ? '-rotate-90' : 'rotate-90'" />
      </div>
    </template>
    <template #option="{ business_legal_name, created_at, active }">
      <div class="w-[96vw] cursor-pointer p-2.5 text-sm md:w-72" :class="{ 'bg-blue-100': active }">
        <div class="font-semibold text-branding-description">{{ business_legal_name }}</div>
        <div>{{ formatDate(created_at) }}</div>
      </div>
    </template>
  </input-dropdown>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import InputDropdown from "@/components/input/InputDropdown.vue";
import IconArrow from "@/components/icons/IconArrow.vue";
import useApplicationsState from "@/composables/state/applicationsState";
import { useResponsive } from "@/composables/common";
import { formatDate } from "@/util/formatting";
import usePlatformScreenState from "@/composables/state/platformScreenState";

const { selectedApplication, allApplications, getApplicationAndSetAsSelected } =
  useApplicationsState();

const { selectedTab } = usePlatformScreenState();

const { isMobile } = useResponsive();

const availableApplications = computed(() => {
  const workflowTemplatesForTab = selectedTab.value?.workflow_template_ids;

  if (!workflowTemplatesForTab?.length) {
    return allApplications.value;
  }

  const result = allApplications.value.filter((application) =>
    workflowTemplatesForTab.includes(application.workflow_template_id)
  );

  return result;
});

const currentApplicationId = computed<string>({
  get() {
    return selectedApplication.value?.id ?? "";
  },
  set(applicationId: string) {
    getApplicationAndSetAsSelected(applicationId);
  },
});

const xOffset = computed(() => (isMobile.value ? 11 : 0));

watch(
  () => selectedTab.value,
  () => {
    if (
      availableApplications.value.some(
        (application) => application.id === selectedApplication.value?.id
      )
    ) {
      return;
    }

    const firstAvailableApplication = availableApplications.value[0];

    if (!firstAvailableApplication) {
      return;
    }

    getApplicationAndSetAsSelected(firstAvailableApplication.id);
  }
);
</script>
